import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, X, Send, Bot, ChevronRight } from "lucide-react";
import axios from "axios";
import { cn } from "../../lib/utils";
import { CONTACT_INFO } from "../../data/mockData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WA = `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(
  "Hello Yantraksh Projects, I'd like to discuss a requirement."
)}`;

// Guided, option-based conversation flow with redirect links
const FLOW = {
  start: {
    bot: "Hi! I'm Yantra 👋 Welcome to Yantraksh Projects. How can I help you today?",
    options: [
      { label: "🏗️ Explore Our Services", next: "services" },
      { label: "🔧 Understand My Requirement", next: "requirement" },
      { label: "📋 Request a Consultation / Quote", next: "consult" },
      { label: "📰 Insights & Case Studies", next: "resources" },
      { label: "📞 Talk to Our Team", next: "contact" },
      { label: "💬 Ask a Question", next: "ai" },
    ],
  },
  services: {
    bot: "We deliver integrated industrial solutions under one roof. Which area interests you?",
    links: [
      { label: "Mechanical & Structural", to: "/services" },
      { label: "Civil & Electrical Infrastructure", to: "/services" },
      { label: "Predictive Plant O&M", to: "/services" },
      { label: "Environmental & Regulatory", to: "/services" },
      { label: "📋 Request a Quote", to: "/contact" },
    ],
    back: "start",
  },
  requirement: {
    bot: "Let's understand your requirement. What type of project is it?",
    options: [
      { label: "Greenfield (new project)", next: "req_industry" },
      { label: "Brownfield (expansion / upgrade)", next: "req_industry" },
      { label: "Maintenance / O&M only", next: "req_industry" },
      { label: "Regulatory / Clearances", next: "req_industry" },
    ],
    back: "start",
  },
  req_industry: {
    bot: "Great. Which industry are you in?",
    options: [
      { label: "Mining & Minerals", next: "req_done" },
      { label: "Steel & Metals", next: "req_done" },
      { label: "Manufacturing", next: "req_done" },
      { label: "Other", next: "req_done" },
    ],
    back: "requirement",
  },
  req_done: {
    bot: "Perfect — our experts can scope this precisely for you. Share a few details and our team will respond within one business day.",
    links: [
      { label: "📋 Open Consultation Form", to: "/contact" },
      { label: "💬 Discuss on WhatsApp", href: WA },
    ],
    back: "start",
    backLabel: "↺ Start Over",
  },
  consult: {
    bot: "We'd love to help with your project. Choose how you'd like to proceed:",
    links: [
      { label: "📋 Fill Consultation Form", to: "/contact" },
      { label: "💬 Chat on WhatsApp", href: WA },
      { label: `📞 Call ${CONTACT_INFO.phone}`, href: `tel:${CONTACT_INFO.phone}` },
    ],
    back: "start",
  },
  resources: {
    bot: "Explore our latest thinking and proven results:",
    links: [
      { label: "📰 Read Insights & Blog", to: "/insights" },
      { label: "🏆 View Case Studies", to: "/case-studies" },
    ],
    back: "start",
  },
  contact: {
    bot: "Reach our team directly through any of these:",
    links: [
      { label: "💬 WhatsApp", href: WA },
      { label: `📞 Call ${CONTACT_INFO.phone}`, href: `tel:${CONTACT_INFO.phone}` },
      { label: `✉️ ${CONTACT_INFO.email}`, href: `mailto:${CONTACT_INFO.email}` },
      { label: "📋 Contact Form", to: "/contact" },
    ],
    back: "start",
  },
  ai: {
    bot: "Sure! Type your question below and I'll answer it for you.",
    aiMode: true,
    back: "start",
    backLabel: "← Back to Menu",
  },
};

const genSessionId = () =>
  `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const ChatWidget = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(genSessionId);
  const [node, setNode] = useState("start");
  const [messages, setMessages] = useState([{ role: "bot", content: FLOW.start.bot }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, node, open]);

  const toggleOpen = () => {
    setOpen((o) => {
      const next = !o;
      if (next) {
        // fresh greeting each time the panel is opened
        setNode("start");
        setMessages([{ role: "bot", content: FLOW.start.bot }]);
      }
      return next;
    });
  };

  const goToNode = (id, userLabel) => {
    const target = FLOW[id];
    if (!target) return;
    setMessages((prev) => [
      ...prev,
      ...(userLabel ? [{ role: "user", content: userLabel }] : []),
      { role: "bot", content: target.bot },
    ]);
    setNode(id);
  };

  const handleLink = (link) => {
    setMessages((prev) => [...prev, { role: "user", content: link.label }]);
    if (link.to) {
      navigate(link.to);
      setOpen(false);
    } else if (link.href) {
      window.open(link.href, "_blank", "noopener,noreferrer");
    }
  };

  const sendAi = async () => {
    const content = input.trim();
    if (!content || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat/`, { session_id: sessionId, message: content });
      setMessages((prev) => [
        ...prev,
        { role: "bot", content: res.data.reply || "Sorry, I couldn't process that.", leadSaved: res.data.lead_saved },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "bot", content: `Sorry, I'm having trouble. Reach us at ${CONTACT_INFO.email} or ${CONTACT_INFO.phone}.` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const current = FLOW[node];

  return (
    <>
      <button
        data-testid="chat-launcher-btn"
        onClick={toggleOpen}
        aria-label="Open assistant"
        className="fixed bottom-24 right-6 z-40 h-14 w-14 rounded-full bg-primary text-white flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-300 border-2 border-gold"
      >
        {open ? <X size={24} /> : <MessageSquare size={24} />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-44 right-6 z-40 w-[92vw] max-w-sm h-[64vh] max-h-[560px] bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden border border-gray-200 animate-fade-in-up"
        >
          {/* Header */}
          <div className="bg-primary text-white px-4 py-3 flex items-center gap-3 shrink-0">
            <div className="h-9 w-9 rounded-full bg-gold flex items-center justify-center text-slate-900">
              <Bot size={20} />
            </div>
            <div>
              <p className="font-display font-bold text-sm uppercase tracking-wider leading-none">Yantra Assistant</p>
              <p className="text-xs text-gray-300 mt-1">Predict. Prevent. Perform.</p>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 scrollbar-thin" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                <div
                  className={cn(
                    "max-w-[85%] px-3 py-2 text-sm rounded-lg whitespace-pre-wrap leading-relaxed",
                    m.role === "user"
                      ? "bg-primary text-white rounded-br-none"
                      : "bg-white text-gray-800 border border-gray-100 rounded-bl-none shadow-sm"
                  )}
                >
                  {m.content}
                  {m.leadSaved && (
                    <span className="block mt-2 text-xs text-green-600 font-medium">✓ Details captured — our team will reach out soon.</span>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-100 rounded-lg rounded-bl-none px-3 py-2 shadow-sm">
                  <div className="flex gap-1 items-center">
                    <span className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}

            {/* Interactive options / links for current node */}
            {!loading && (
              <div className="space-y-2 pt-1">
                {(current.options || []).map((opt) => (
                  <button
                    key={opt.label}
                    data-testid="chat-option"
                    onClick={() => goToNode(opt.next, opt.label)}
                    className="w-full text-left text-sm border border-primary/30 text-primary px-3 py-2 rounded-md hover:bg-primary hover:text-white transition-colors flex items-center justify-between group"
                  >
                    <span>{opt.label}</span>
                    <ChevronRight size={16} className="opacity-50 group-hover:translate-x-1 transition-transform" />
                  </button>
                ))}

                {(current.links || []).map((link) => (
                  <button
                    key={link.label}
                    data-testid="chat-link"
                    onClick={() => handleLink(link)}
                    className="w-full text-left text-sm bg-gold text-slate-900 font-medium px-3 py-2 rounded-md hover:bg-gold/90 transition-colors flex items-center justify-between"
                  >
                    <span>{link.label}</span>
                    <ChevronRight size={16} />
                  </button>
                ))}

                {current.back && (
                  <button
                    data-testid="chat-back"
                    onClick={() => goToNode(current.back, current.backLabel || "← Back")}
                    className="text-xs text-gray-500 hover:text-primary px-1 pt-1"
                  >
                    {current.backLabel || "← Back"}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Input — only in AI mode */}
          {current.aiMode && (
            <form
              onSubmit={(e) => { e.preventDefault(); sendAi(); }}
              className="p-3 border-t border-gray-100 bg-white flex items-center gap-2 shrink-0"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question..."
                className="flex-1 text-sm border border-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <button
                type="submit"
                data-testid="chat-send-btn"
                disabled={loading || !input.trim()}
                className="h-9 w-9 rounded-full bg-primary text-white flex items-center justify-center disabled:opacity-50 hover:bg-primary/90 transition-colors"
              >
                <Send size={16} />
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
